/**
 * CaseSummaryCard — self-updating AI summary of a case.
 *
 * The summary is produced by a backend background job; there is NO manual refresh
 * button. This card just displays the current summary + its freshness, and surfaces
 * subtle "Nye oppdateringer" / "Oppdaterer…" states when the backend reports that
 * new (not-yet-summarized) content exists or a generation is in progress.
 *
 * Freshness strategy (auto-only):
 *  - refetch when the case feed changes (via the `refreshSignal` prop), and
 *  - poll on a light interval while mounted (faster while generating / new updates).
 *
 * Errors are quiet and non-blocking: the card degrades to a subtle inline message and
 * keeps any previously loaded summary visible. It is PINNED (sticky) at the top of the
 * feed scroll area so it stays visible while the feed scrolls — on both desktop and
 * mobile — and is compact by default (2-line preview) with a Vis mer/Vis mindre toggle.
 *
 * Mobile (≤860px): starts as a one-row peek (head only); tap to reveal a 1-line clamp,
 * then Vis mer for the full text — progressive disclosure like previous-work AI cards.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { SparklesIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { fetchCaseSummary } from "../../../api/case";
import { prepareDisplayHtml } from "../../../utils/sanitizeHtml";
import { usePolling } from "../../../hooks/usePolling";

const POLL_IDLE_MS = 45000;
const POLL_ACTIVE_MS = 12000;
/** Match CaseView mobile breakpoint (case-view-redesign.css). */
const MOBILE_MQ = "(max-width: 860px)";

function useIsMobileCaseView() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return isMobile;
}

/** Format an ISO8601 timestamp into a short localized date-time, or "" if invalid. */
function formatUpdatedAt(iso, language) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(language || "nb-NO", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

export function CaseSummaryCard({ caseId, refreshSignal }) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobileCaseView();
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  /** Mobile peek: head-only until the user taps to reveal the clamped body. */
  const [peekOpen, setPeekOpen] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return !window.matchMedia(MOBILE_MQ).matches;
  });
  const [isOverflowing, setIsOverflowing] = useState(false);
  const mountedRef = useRef(true);
  const hasLoadedRef = useRef(false);
  const bodyRef = useRef(null);

  const language = i18n?.language;

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!caseId) return;
      if (!silent && !hasLoadedRef.current) setStatus("loading");

      const result = await fetchCaseSummary(caseId, language);
      if (!mountedRef.current) return;

      if (result?.__error) {
        // Quiet, non-blocking: keep any existing summary; only flag error when we
        // have nothing to show yet.
        setStatus((prev) => (hasLoadedRef.current ? prev : "error"));
        return;
      }

      hasLoadedRef.current = true;
      setData(result);
      setStatus("ready");
    },
    [caseId, language]
  );

  // Initial load + reload when case or locale changes.
  useEffect(() => {
    mountedRef.current = true;
    hasLoadedRef.current = false;
    setData(null);
    setStatus("idle");
    setExpanded(false);
    setPeekOpen(
      typeof window !== "undefined" && window.matchMedia
        ? !window.matchMedia(MOBILE_MQ).matches
        : true
    );
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [caseId, language, load]);

  // Reset mobile peek when crossing the breakpoint.
  useEffect(() => {
    if (!isMobile) {
      setPeekOpen(true);
      return;
    }
    setPeekOpen(false);
    setExpanded(false);
  }, [isMobile]);

  // `enabled` is a newer backend field. Treat a MISSING value as enabled (true) so an
  // older backend that doesn't send it keeps working (backward-compatible).
  const featureDisabled = data?.enabled === false;
  const summaryText =
    typeof data?.ai_summary === "string" ? data.ai_summary.trim() : "";

  // Refetch when the feed changes (new item posted / items reloaded). Skip the very
  // first run so we don't double-fetch alongside the initial load effect. When the
  // feature is disabled it will never (re)generate, so we don't refetch.
  const firstSignalRef = useRef(true);
  useEffect(() => {
    if (firstSignalRef.current) {
      firstSignalRef.current = false;
      return;
    }
    if (featureDisabled) return;
    load({ silent: true });
  }, [refreshSignal, featureDisabled, load]);

  // Light polling while the view is open; quicker while the backend is working. Never
  // poll when the feature is disabled — nothing will change, so avoid pointless
  // requests and an endless "waiting" state.
  const isBusy = Boolean(data?.generating || data?.has_new_updates);
  usePolling(() => load({ silent: true }), isBusy ? POLL_ACTIVE_MS : POLL_IDLE_MS, {
    enabled: Boolean(caseId) && !featureDisabled,
  });

  const updatedLabel = formatUpdatedAt(data?.ai_summary_updated_at, language);
  // A disabled feature never regenerates, so suppress the "working" affordances.
  const generating = Boolean(data?.generating) && !featureDisabled;
  const hasNewUpdates = Boolean(data?.has_new_updates) && !featureDisabled;
  const hasBody = status === "ready" && Boolean(summaryText);
  // Clear "not enabled" state: disabled AND nothing stored to show.
  const disabledEmpty = status === "ready" && featureDisabled && !summaryText;

  const showBody = !isMobile || peekOpen;

  // Only show the Vis mer/Vis mindre toggle when the collapsed body actually
  // overflows its clamp. Measured against the clamped element.
  useLayoutEffect(() => {
    if (!hasBody || expanded || !showBody) return;
    const el = bodyRef.current;
    if (!el) {
      setIsOverflowing(false);
      return;
    }
    setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [hasBody, expanded, summaryText, language, showBody]);

  if (!caseId) return null;

  const renderBody = () => {
    if (status === "loading") {
      return (
        <div className="cv-summary-skeleton" aria-hidden="true">
          <span className="cv-summary-skeleton-line" />
          <span className="cv-summary-skeleton-line short" />
        </div>
      );
    }
    if (status === "error") {
      return <p className="cv-summary-error">{t("case.summary.error")}</p>;
    }
    if (disabledEmpty) {
      return <p className="cv-summary-disabled">{t("case.summary.disabled")}</p>;
    }
    if (!summaryText) {
      return <p className="cv-summary-empty">{t("case.summary.empty")}</p>;
    }
    const summaryHtml = prepareDisplayHtml(summaryText);
    return (
      <div
        ref={bodyRef}
        className={`cv-summary-body ${expanded ? "is-expanded" : "is-clamped"}`}
        dangerouslySetInnerHTML={{ __html: summaryHtml }}
      />
    );
  };

  const showToggle = hasBody && showBody && (expanded || isOverflowing);
  const headInteractive = isMobile;

  const onHeadActivate = () => {
    if (!isMobile) return;
    if (!peekOpen) {
      setPeekOpen(true);
      return;
    }
    setPeekOpen(false);
    setExpanded(false);
  };

  return (
    <section
      className={`cv-summary-card ${expanded ? "is-open" : ""} ${
        isMobile && !peekOpen ? "is-peek" : ""
      }`}
      aria-label={t("case.summary.title")}
    >
      <div
        className={`cv-summary-head ${headInteractive ? "is-interactive" : ""}`}
        role={headInteractive ? "button" : undefined}
        tabIndex={headInteractive ? 0 : undefined}
        aria-expanded={headInteractive ? peekOpen : undefined}
        onClick={headInteractive ? onHeadActivate : undefined}
        onKeyDown={
          headInteractive
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onHeadActivate();
                }
              }
            : undefined
        }
      >
        <span className="cv-summary-icon" aria-hidden="true">
          <SparklesIcon className="h-4 w-4" />
        </span>
        <span className="cv-summary-title">{t("case.summary.title")}</span>
        <div className="cv-summary-flags">
          {generating && (
            <span className="cv-summary-generating" role="status">
              <span className="cv-summary-dot" aria-hidden="true" />
              {t("case.summary.generating")}
            </span>
          )}
          {!generating && hasNewUpdates && (
            <span className="cv-summary-badge is-new">
              {t("case.summary.new_updates")}
            </span>
          )}
          {isMobile && (
            <ChevronDownIcon
              className={`cv-summary-peek-chevron h-4 w-4 ${peekOpen ? "is-open" : ""}`}
              aria-hidden="true"
            />
          )}
        </div>
      </div>

      {showBody && renderBody()}

      {showBody && hasBody && (updatedLabel || showToggle) && (
        <div className="cv-summary-foot">
          {updatedLabel ? (
            <span className="cv-summary-updated">
              {t("case.summary.updated", { time: updatedLabel })}
            </span>
          ) : (
            <span />
          )}
          {showToggle && (
            <button
              type="button"
              className="cv-summary-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              aria-expanded={expanded}
            >
              {expanded ? t("case.summary.show_less") : t("case.summary.show_more")}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

export default CaseSummaryCard;
